import React from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/core';
import {
  ssColorMidGrey, ssColorBlack, mediaBreakpointUpLG, mediaBreakpointDownLG,
} from '../constants/style-constants';
import { hunit, vunit } from '../lib/style-helpers';
import DashboardCard from './DashboardCard';
import { Header4, Paragraph } from './Typography';
import TickIcon from './icons/TickIcon';

const completedCardCss = css`
  display: flex;
  padding: ${vunit(3)} ${hunit(4)};
  text-align: center;

  ${mediaBreakpointDownLG} {
    padding-bottom: ${vunit(3)};
    padding-top: ${vunit(3)};
  }

  ${mediaBreakpointUpLG} {
    padding-right: ${hunit(4)};
  }
`;

const completedCardInnerCss = css`
  align-items: center;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  justify-content: center;
`;

const completedIconCss = css`
  height: 45px;
  display: inline-block;
  opacity: 0.5;
  margin: 0 0 ${vunit(2)};
  width: 45px;

  svg {
    fill: ${ssColorBlack};
  }
`;

const completedHeaderCss = css`
  margin-bottom: 0;
`;

const completedCopyCss = css`
  margin: 0;
  padding: 0;
  text-transform: uppercase;
`;

const CompletedGameCard = ({ name, phrases }) => (
  <DashboardCard showIcon={false} extraCss={[completedCardCss]}>
    <div css={completedCardInnerCss}>
      <span css={completedIconCss}><TickIcon /></span>
      <Header4 css={completedHeaderCss}>{name}</Header4>
      <Paragraph css={completedCopyCss}>
        {phrases.games}
        {' '}
        {phrases.completed}
      </Paragraph>
    </div>
  </DashboardCard>
);
CompletedGameCard.propTypes = {
  name: PropTypes.string.isRequired,
  phrases: PropTypes.shape({
    games: PropTypes.string.isRequired,
    completed: PropTypes.string.isRequired,
  }).isRequired,
};

const activeCardCss = css`
  padding: ${vunit(5)};
  padding-right: ${vunit(9)};
`;

const activeCardGameCountCss = css`
  color: ${ssColorMidGrey};
  display: inline-block;
  font-size: 0.9rem;
  font-weight: normal;
  text-transform: uppercase;
`;

const ActiveGameCard = ({
  name,
  description,
  remaining,
  phrases,
  path,
}) => (
  <DashboardCard href={path} extraCss={[activeCardCss]}>
    <Header4>
      {name}
      {' '}
      <span css={activeCardGameCountCss}>
        {remaining}
        {' '}
        {phrases.games}
      </span>
    </Header4>
    <Paragraph>{description}</Paragraph>
  </DashboardCard>
);
ActiveGameCard.propTypes = {
  name: PropTypes.string.isRequired,
  remaining: PropTypes.number.isRequired,
  description: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  phrases: PropTypes.shape({
    games: PropTypes.string.isRequired,
  }).isRequired,
};

const GameCards = ({ games }) => (
  games.map(game => (
    game.completed
      ? <CompletedGameCard key={game.name} {...game} />
      : <ActiveGameCard key={game.name} {...game} />
  ))
);
GameCards.propTypes = {
  games: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
    remaining: PropTypes.number.isRequired,
    completed: PropTypes.bool.isRequired,
    phrases: PropTypes.shape({
      completed: PropTypes.string.isRequired,
      games: PropTypes.string.isRequired,
    }),
  })).isRequired,
};

export default GameCards;
